import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../../../services/authService';
import axios from '../../../utils/axiosInstance';

// dayjs 한국어 설정
dayjs.locale('ko');

// 다크 모드 스타일 상수
const darkModeStyles = {
  container: {
    p: 2,
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    minHeight: '100vh',
  },
  paper: {
    p: 3,
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
  },
  title: {
    color: '#fff',
    fontSize: '28px',
    fontWeight: 'bold',
    mb: 1,
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      color: '#fff',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(96, 165, 250, 0.3)',
        },
      },
    },
  },
  menuItemHighlight: {
    '&.MuiMenuItem-root': {
      color: '#ce93d8',
      fontWeight: 'bold',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(206, 147, 216, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(206, 147, 216, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(206, 147, 216, 0.3)',
        },
      },
    },
  },
  datePickerTextField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '& .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  timeInputField: {
    width: '44px',
    '& .MuiInputBase-root': {
      height: '40px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 6px',
      textAlign: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  searchButton: {
    backgroundColor: '#60a5fa',
    color: '#fff',
    borderRadius: '10px',
    padding: '10px 24px',
    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
    fontWeight: 'bold',
    textTransform: 'none',
    transition: 'all 0.3s ease',
    height: '56px',
    '&:hover': {
      backgroundColor: '#3b82f6',
      boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
      transform: 'translateY(-2px)',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    mb: 3,
  },
  tableHead: {
    backgroundColor: 'rgba(45, 45, 65, 0.9)',
    '& .MuiTableCell-head': {
      color: '#fff',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transition: 'background-color 0.3s ease',
    },
    '& .MuiTableCell-body': {
      color: '#fff',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
  },
  tableFooter: {
    backgroundColor: 'rgba(156, 39, 176, 0.15)',
    '& .MuiTableCell-root': {
      color: '#ce93d8',
      fontWeight: 'bold',
      borderBottom: '0px',
    },
  },
  tableCell: {
    color: '#fff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  tableCellNegative: {
    color: '#f87171',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  tablePagination: {
    marginTop: '12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(45, 45, 65, 0.8)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '& .MuiTablePagination-toolbar': {
      justifyContent: 'center',
    },
    '& .MuiTablePagination-select': {
      padding: '6px 10px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
      fontWeight: 'bold',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '& .MuiSelect-icon': {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
    '& .MuiTablePagination-actions': {
      color: '#fff',
      '& .MuiIconButton-root': {
        color: 'rgba(255, 255, 255, 0.7)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.3)',
        },
      },
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
  loadingIndicator: {
    color: '#60a5fa',
  },
  noDataBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100px',
  },
  noDataMessage: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontStyle: 'italic',
  },
  // 메뉴 스타일
  menu: {
    '& .MuiPaper-root': {
      backgroundColor: 'rgba(35, 35, 50, 0.98)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      width: '150px',
      color: '#fff',
    },
    '& .MuiMenuItem-root': {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    '& .MuiDivider-root': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  menuDivider: {
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    mt: 1,
    pt: 1,
  },
  menuHeader: {
    pl: 2,
    color: '#ce93d8',
    fontWeight: 'bold',
  },
};

// 수정된 CustomDateTimePicker 컴포넌트 - 다크모드 캘린더/시간 선택기 스타일 적용
const CustomDateTimePicker = ({ label, value, onChange }) => {
  return (
    <DateTimePicker
      label={label}
      value={value}
      onChange={(newValue) => {
        if (!newValue) return;
        console.log(`${label} 변경:`, newValue.format('YYYY-MM-DD HH:mm:ss'));
        onChange(newValue);
      }}
      format="YYYY-MM-DD HH:mm"
      ampm={false}
      slotProps={{
        textField: {
          sx: darkModeStyles.datePickerTextField,
          fullWidth: true,
        },
        popper: {
          sx: {
            '& .MuiPaper-root': {
              backgroundColor: 'rgba(35, 35, 50, 0.95)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              '& .MuiPickersDay-root': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                  color: '#fff',
                },
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.2)',
                },
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiPickersCalendarHeader-label': {
                color: '#fff',
              },
              '& .MuiPickersCalendarHeader-switchViewButton': {
                color: '#fff',
              },
              '& .MuiIconButton-root': {
                color: '#fff',
              },
              '& .MuiClock-pin': {
                backgroundColor: '#60a5fa',
              },
              '& .MuiClockPointer-root': {
                backgroundColor: '#60a5fa',
              },
              '& .MuiClockPointer-thumb': {
                border: '14px solid #60a5fa',
                backgroundColor: '#60a5fa',
              },
              '& .MuiClock-clock': {
                backgroundColor: 'rgba(50, 50, 70, 0.95)',
              },
              '& .MuiClockNumber-root': {
                color: '#fff',
              },
              '& .MuiPickersYear-yearButton': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                  color: '#fff',
                },
              },
              '& .MuiPickersMonth-root': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                },
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: '#fff',
              },
              '& .MuiButtonBase-root': {
                color: '#fff',
              },
              '& .MuiButton-root': {
                color: '#60a5fa',
              },
              '& .MuiPickersDay-today': {
                border: '1px solid #60a5fa',
              },
            },
          },
        },
      }}
    />
  );
};

const MoneyReceiveHistory = () => {
  const isNonMobile = useMediaQuery('(min-width:600px)');
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    agentId: '',
    type: '',
    gameCompany: '',
    startDate: dayjs().hour(0).minute(0).second(0), // 오늘 00:00:00
    endDate: dayjs().hour(23).minute(59).second(59), // 오늘 날짜, 종료는 23:59:59
  });

  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [agentList, setAgentList] = useState([]); // 에이전트 목록

  // 현재 로그인한 유저 정보 가져오기
  useEffect(() => {
    const user = getUser();
    if (user && user.id) {
      setCurrentUser(user);
      // 기본 에이전트 ID를 현재 사용자로 설정
      setFilters(prev => ({
        ...prev,
        agentId: user.id
      }));
    }
  }, []);

  // 에이전트 목록 가져오기
  useEffect(() => {
    fetchAgentList();
  }, []);

  // 필터 변경 시 자동으로 데이터 가져오기
  useEffect(() => {
    if (filters.agentId) {
      fetchHistoryData();
    }
  }, [filters.agentId]);

  // 에이전트 목록 가져오기 함수
  const fetchAgentList = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/agent/children');
      
      if (response.data && response.data.success) {
        const agentTree = response.data.agent_tree;
        
        // 현재 로그인한 에이전트와 하위 에이전트들을 목록에 추가
        const agents = [
          { id: agentTree.id, name: agentTree.id, level: 0 }, // 현재 로그인한 에이전트
          ...agentTree.children.map(child => ({
            id: child.id,
            name: child.id,
            level: child.level,
          })),
        ];
        
        setAgentList(agents);
        
        // 현재 사용자 ID를 기본 선택 값으로 설정
        const user = getUser();
        if (user && user.id) {
          setFilters(prev => ({
            ...prev,
            agentId: user.id
          }));
        } else if (agents.length > 0) {
          setFilters(prev => ({
            ...prev,
            agentId: agents[0].id
          }));
        }
      }
    } catch (err) {
      console.error('에이전트 목록 가져오기 실패:', err);
      setError('에이전트 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 내역 데이터 가져오기 함수
  const fetchHistoryData = async () => {
    setLoading(true);
    try {
      // 날짜/시간 포맷팅
      const startDateFormatted = filters.startDate.format('YYYY-MM-DD HH:mm:ss');
      const endDateFormatted = filters.endDate.format('YYYY-MM-DD HH:mm:ss');
      
      // 선택된 에이전트 ID 또는 기본값
      const agentId = filters.agentId || (currentUser && currentUser.id);
      
      if (!agentId) {
        setError('에이전트 ID가 선택되지 않았습니다.');
        setLoading(false);
        return;
      }
      
      console.log('요청 파라미터:', {
        agent_id: agentId,
        start: startDateFormatted,
        end: endDateFormatted,
        type: filters.type
      });
      
      let url = `/agent/point/logs?agent_id=${agentId}&start=${startDateFormatted}&end=${endDateFormatted}`;
      
      // 타입 필터가 있으면 추가
      if (filters.type) {
        url += `&type=${filters.type}`;
      }
      
      const response = await axios.get(url);

      if (response.data) {
        console.log('API 응답 데이터::', response.data);
        setHistoryList(response.data.data || []);
        setError(null);
      }
    } catch (error) {
      console.error('데이터 불러오기 중 오류 발생:', error);
      setError('데이터를 불러오는 중 오류가 발생했습니다.');
      setHistoryList([]);
    } finally {
      setLoading(false);
    }
  };

  const totalChargeAmount = historyList.reduce((acc, row) => acc + (row.amount || 0), 0);

  const handleFilterChange = (field, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    console.log('검색 조건:', filters);
    fetchHistoryData();
  };

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  // 페이지 변경 핸들러
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 보여지는 행 개수 변경 핸들러
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const handleMenuOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleMenuItemClick = action => {
    if (action === 'edit') {
      navigate(`/agent/update/${selectedRowId}`);
    }

    if (action === 'history') {
      navigate(`/money-send-history?agentId=${selectedRowId}`);
    }

    if (action === 'charge') {
      navigate(`/money-charge-history?agentId=${selectedRowId}`);
    }

    if (action === 'user') {
      navigate(`/user-list?agentId=${selectedRowId}`);
    }

    if (action === 'payment') {
      navigate(`/money?agentId=${selectedRowId}`);
    }
    
    handleMenuClose();
  };

  // 금액 포맷팅 함수 - 음수는 0으로 표시
  const formatNumber = (number) => {
    if (number === null || number === undefined) return '0';
    const formattedNumber = parseFloat(number) < 0 ? 0 : number;
    return formattedNumber.toLocaleString();
  };

  return (
    <Box sx={darkModeStyles.container}>
      <Paper sx={darkModeStyles.paper}>
        <Typography variant="h4" sx={darkModeStyles.title}>
          지급받은 내역
        </Typography>

        {/* 필터 영역 */}
        <Box
          display="flex"
          flexDirection={isNonMobile ? 'row' : 'column'}
          flexWrap="wrap"
          gap={isNonMobile ? 2 : 1.5}
          alignItems={isNonMobile ? 'center' : 'stretch'}
          sx={{
            mb: 3,
            '& > *': { flex: isNonMobile ? 1 : 'none', minWidth: isNonMobile ? '200px' : '100%' },
          }}
        >
          {/* 에이전트 아이디 필터 */}
          <FormControl sx={darkModeStyles.formControl}>
            <InputLabel>에이전트 아이디</InputLabel>
            <Select
              value={filters.agentId}
              onChange={e => handleFilterChange('agentId', e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(35, 35, 50, 0.95)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              {agentList.map(agent => (
                <MenuItem 
                  key={agent.id} 
                  value={agent.id} 
                  sx={agent.grade === 'admin' ? darkModeStyles.menuItemHighlight : darkModeStyles.menuItem}
                >
                  {/* admin이고 level이 0인 경우(자기 자신) 특별한 표시 */}
                  {currentUser?.grade === 'admin' && agent.level === 0 
                    ? '에이전트 아이디를 선택하세요' 
                    : agent.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 타입 필터 */}
          <FormControl sx={darkModeStyles.formControl}>
            <InputLabel>타입</InputLabel>
            <Select
              value={filters.type}
              onChange={e => handleFilterChange('type', e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(35, 35, 50, 0.95)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              <MenuItem value="" sx={darkModeStyles.menuItem}>
                전체
              </MenuItem>
              <MenuItem value="지급" sx={darkModeStyles.menuItem}>
                지급
              </MenuItem>
              <MenuItem value="회수" sx={darkModeStyles.menuItem}>
                회수
              </MenuItem>
            </Select>
          </FormControl>

          {/* 날짜 필터 */}
          <Box flex={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box display="flex" flexDirection={isNonMobile ? 'row' : 'column'} gap={isNonMobile ? 2 : 1}>
                <CustomDateTimePicker label="시작 날짜 및 시간" value={filters.startDate} onChange={date => handleFilterChange('startDate', date)} />
                <CustomDateTimePicker label="종료 날짜 및 시간" value={filters.endDate} onChange={date => handleFilterChange('endDate', date)} />
              </Box>
            </LocalizationProvider>
          </Box>
          {/* 검색 버튼 */}
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            sx={{
              ...darkModeStyles.searchButton,
              width: isNonMobile ? '100px' : '100%',
              minWidth: '80px',
            }}
          >
            검색
          </Button>
        </Box>

        {/* 에러 메시지 */}
        {error && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: '8px', border: '1px solid rgba(244, 67, 54, 0.3)' }}>
            <Typography sx={{ color: '#f87171' }}>{error}</Typography>
          </Box>
        )}

        {/* 테이블 영역 */}
        <TableContainer sx={darkModeStyles.tableContainer}>
          {loading ? (
            <Box sx={darkModeStyles.loadingBox}>
              <CircularProgress sx={darkModeStyles.loadingIndicator} />
            </Box>
          ) : historyList.length === 0 ? (
            <Box sx={darkModeStyles.noDataBox}>
              <Typography sx={darkModeStyles.noDataMessage}>데이터가 없습니다.</Typography>
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead sx={darkModeStyles.tableHead}>
                <TableRow>
                  <TableCell sx={darkModeStyles.tableCell}>트랜잭션 번호</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>처리자</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>지급 받은 에이전트</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>타입</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>처리 금액</TableCell>
                  {/* <TableCell sx={darkModeStyles.tableCell}>받기 전 금액</TableCell> */}
                  <TableCell sx={darkModeStyles.tableCell}>이후 금액</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>설명</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>처리 시각</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                  <TableRow key={idx} sx={darkModeStyles.tableRow}>
                    <TableCell sx={darkModeStyles.tableCell}>{row.log_id}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{row.processor_id}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{row.agent_id}</TableCell>
                    <TableCell sx={darkModeStyles.tableCellNegative}>{row.type}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{formatNumber(row.amount)} 원</TableCell>
                    {/* <TableCell sx={darkModeStyles.tableCell}>{formatNumber(row.process_amount)} 원</TableCell> */}
                    <TableCell sx={darkModeStyles.tableCell}>{formatNumber(row.after_balance)} 원</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{row.description || '-'}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{row.created_at}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={darkModeStyles.tableFooter}>
                  <TableCell colSpan={4}>총합</TableCell>
                  <TableCell>{formatNumber(totalChargeAmount)} 원</TableCell>
                  <TableCell colSpan={4}></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          )}

          {historyList.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={historyList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage=""
              sx={darkModeStyles.tablePagination}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      '& .MuiMenuItem-root': {
                        color: '#fff',
                        padding: '8px 16px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(96, 165, 250, 0.2)',
                        },
                      },
                    },
                  },
                },
              }}
            />
          )}
        </TableContainer>
      </Paper>

      {/* 메뉴 */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} sx={darkModeStyles.menu}>
        <MenuItem onClick={() => handleMenuItemClick('edit')}>수정</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('history')}>지급 내역</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('charge')}>충전 내역</MenuItem>
        <Box sx={darkModeStyles.menuDivider}>
          <Typography variant="subtitle2" sx={darkModeStyles.menuHeader}>
            유저
          </Typography>
          <MenuItem onClick={() => handleMenuItemClick('user')}>유저 목록</MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('payment')}>지급 내역</MenuItem>
        </Box>
      </Menu>
    </Box>
  );
};

export default MoneyReceiveHistory;